/**
 * What: Renders the primary Shopify embedded app layout and navigation wrapper.
 * Transport / data-flow: Standard layout frame for settings and primary application content.
 *
 */
import type { ReactNode } from 'react'
import type { FeatureEntitlementSnapshot } from '@testfeed/entitlement-contracts'
import type { useAppBridge } from '@shopify/app-bridge-react'
import { Banner, BlockStack, Text } from '@shopify/polaris'
import {
  ShopifyAppContext,
  type ShopifyAppContextValue,
  type VerifiedTestFeedConnectionScope,
} from '../ShopifyAppContext'
import { ShopifyStudyActivityProvider } from '../ShopifyStudyActivityContext'
import { ShopifyAppNavigation } from './ShopifyAppNavigation'
import { ShopifyPageContent } from './ShopifyPageContent'
import {
  type ActionDescriptor,
  ShopifyPrimaryActionButton,
  ShopifySecondaryActionButtons,
} from './ShopifyPageActionButtons'
import { ShopifyStudyActivityBanner } from './ShopifyStudyActivityBanner'

export function ShopifyMainPageView({
  shopContextValue,
  shopify,
  verifiedScope,
  entitlements,
  pageTitle,
  pageSubtitle,
  pagePrimaryAction,
  pageSecondaryActions,
  isFullWidthView,
  showConnectedBanner,
  onDismissConnectedBanner,
  connectedBannerTitle,
  connectedBannerBody,
  connectionRecoverySection,
  primaryContent,
  onOpenResults,
}: {
  shopContextValue: ShopifyAppContextValue
  shopify: ReturnType<typeof useAppBridge>
  verifiedScope: VerifiedTestFeedConnectionScope | null
  entitlements: FeatureEntitlementSnapshot
  pageTitle?: string
  pageSubtitle?: string
  pagePrimaryAction?: ActionDescriptor
  pageSecondaryActions?: ActionDescriptor[]
  isFullWidthView: boolean
  showConnectedBanner: boolean
  onDismissConnectedBanner: () => void
  connectedBannerTitle: string
  connectedBannerBody: string
  connectionRecoverySection: ReactNode
  primaryContent: ReactNode
  onOpenResults: (studyId: string, runId: string | null) => void
}) {
  return (
    <ShopifyAppContext.Provider value={shopContextValue}>
      <ShopifyStudyActivityProvider shopify={shopify} scope={verifiedScope} onOpenResults={onOpenResults}>
        <ShopifyAppNavigation entitlements={entitlements} />
        <s-page heading={pageTitle} inlineSize={isFullWidthView ? 'large' : 'base'}>
          <ShopifyPageContent>
            <BlockStack gap="400">
              {pageSubtitle && (
                <s-section>
                  <s-paragraph color="subdued">{pageSubtitle}</s-paragraph>
                </s-section>
              )}
              <ShopifyStudyActivityBanner />
              <BlockStack gap="400">
                {showConnectedBanner && (
                  <Banner title={connectedBannerTitle} tone="success" onDismiss={onDismissConnectedBanner}>
                    <Text as="p">{connectedBannerBody}</Text>
                  </Banner>
                )}
                {connectionRecoverySection}
                {primaryContent}
              </BlockStack>
            </BlockStack>
          </ShopifyPageContent>
          <ShopifyPrimaryActionButton action={pagePrimaryAction} />
          <ShopifySecondaryActionButtons actions={pageSecondaryActions} />
        </s-page>
      </ShopifyStudyActivityProvider>
    </ShopifyAppContext.Provider>
  )
}
